import { h } from "./../../utils/createElement";
import { __assign, __extends } from "tslib";
import * as React from 'react';
import ReactDOM from 'react-dom';
import { emptyFn } from '../../utils/helper';
import Trigger from '../../utils/Trigger';
import { DropdownContext, getDropdownContext } from './context';
import { consumeTheme } from '../../utils/theme';
import { triggerMicroTask } from '../../utils/microTask';
import { decideKeepDom } from '../../utils/keepDom';
import { DocumentDomContext } from '../../utils/documentDom';
import { restoreFocus, saveFocus, traverseFocusIn } from '../../utils/focus';
import throttle from 'lodash/throttle';
import ua from '@tencent/docs-user-agent';
import { checkPositionChange } from '../../utils/checkPositionChange';
var DEFAULT_METRICS = {
    left: '0',
    top: '0'
};
var DROPDOWN_TRANSITION_OUT_DURATION = 120;
var DROPDOWN_TRANSITION_IN_DURATION = 240;
var outerAlignments = ['auto-outer', 'left-outer', 'right-outer'];
var shouldFixAnimationFlickering = ua.isIOS || ua.isIPadEmulatedMac || ua.isIPad;
var HOVER_DELAY = 100;
var Dropdown = (function (_super) {
    __extends(Dropdown, _super);
    function Dropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.rootDomRef = React.createRef();
        _this.dropContentRef = React.createRef();
        _this.forceRenderDom = false;
        _this.cancelPositionChangeChecker = null;
        _this.resizeObserver = null;
        _this.isInAnimation = false;
        _this.dropContentObserver = new MutationObserver(function (mutationList) {
            if (mutationList.some(function (_a) {
                var addedNodes = _a.addedNodes, removedNodes = _a.removedNodes;
                return addedNodes.length || removedNodes.length;
            })) {
                _this.refreshDropContentMetric();
            }
        });
        _this.state = {
            visible: false,
            dropContentMetrics: __assign({}, DEFAULT_METRICS),
            actualPlacement: 'bottom',
            actualAlignment: _this.isOuterAlignment ? 'left-outer' : 'left',
        };
        _this.refreshDropContentMetric = throttle(function () {
            _this.removePreviousClampedSize(_this.updateDropContentMetric);
        }, 20);
        _this.updateDropContentMetric = function () {
            var rootDom = _this.rootDomRef.current;
            var dropContentDom = _this.dropContentRef.current;
            if (!rootDom || !dropContentDom) {
                return;
            }
            var rootDomRect = rootDom.getBoundingClientRect();
            var dropContentDomRect = dropContentDom.getBoundingClientRect();
            var _a = _this.calcHorizontalMetric(rootDomRect, dropContentDomRect), actualAlignment = _a.actualAlignment, horizontalMetric = _a.metric;
            var _b = _this.calcVerticalMetric(rootDomRect, dropContentDomRect), actualPlacement = _b.actualPlacement, verticalMetric = _b.metric;
            var dropContentMetrics = __assign(__assign({}, horizontalMetric), verticalMetric);
            _this.setState({ dropContentMetrics: dropContentMetrics, actualPlacement: actualPlacement, actualAlignment: actualAlignment });
        };
        _this.hide = function (e) {
            if (e && _this.consumeKeep(e)) {
                return;
            }
            if (!_this.isVisible()) {
                return;
            }
            if (!_this.isControlled) {
                _this.restorePreviousFocus();
            }
            _this.setStateVisible(false);
        };
        _this.show = function (preventStateVisibleUpdate) {
            _this.forceRenderDom = true;
            _this.removePreviousClampedSize(function () {
                requestAnimationFrame(function () {
                    _this.forceRenderDom = false;
                    _this.updateDropContentMetric();
                    (!preventStateVisibleUpdate) && _this.setStateVisible(true);
                });
            });
        };
        _this.toggle = function () {
            if (_this.props.disabled) {
                return;
            }
            var visible = !_this.isVisible();
            if (visible) {
                _this.show();
            }
            else {
                _this.hide();
            }
        };
        _this.handleMouseEnter = function () {
            if (_this.props.disabled) {
                return;
            }
            clearTimeout(_this.hideTimeout);
            clearTimeout(_this.showTimeout);
            if (!_this.isVisible()) {
                _this.showTimeout = setTimeout(_this.show, HOVER_DELAY);
            }
        };
        _this.handleMouseLeave = function () {
            if (_this.props.disabled || _this.isInAnimation) {
                return;
            }
            clearTimeout(_this.showTimeout);
            _this.hideTimeout = setTimeout(function () { return !_this.isInAnimation && _this.hide(); }, HOVER_DELAY);
        };
        _this.handleKeydown = function (e) {
            if (e.key === 'Escape') {
                _this.hide();
            }
            else if (e.key === 'Tab') {
                _this.traverseFocus(e);
            }
        };
        _this.themedRender = function (classNames) {
            var _a, _b, _c;
            var _d = _this.props, prefixCls = _d.prefixCls, className = _d.className, style = _d.style, children = _d.children, dropContent = _d.dropContent, disabled = _d.disabled, dropContentStyle = _d.dropContentStyle, dropContentClassName = _d.dropContentClassName, forcedZIndex = _d.zIndex, animated = _d.animated, trigger = _d.trigger, destroyOnClose = _d.destroyOnClose, containerDom = _d.containerDom, testId = _d.testId, dropContentTestId = _d.dropContentTestId, containerFollowVisible = _d.containerFollowVisible, containerClassName = _d.containerClassName, containerStyle = _d.containerStyle;
            var _e = _this.state, dropContentMetrics = _e.dropContentMetrics, actualPlacement = _e.actualPlacement, actualAlignment = _e.actualAlignment;
            var visible = _this.isVisible();
            var contextZIndex = _this.context.zIndex;
            var zIndex = forcedZIndex || contextZIndex;
            var cls = classNames(prefixCls, className, prefixCls + "-" + (visible ? 'visible' : 'hidden'), (_a = {},
                _a[prefixCls + "-disabled"] = disabled,
                _a));
            var contentCls = classNames(prefixCls + "-content", dropContentClassName, prefixCls + "-content-" + actualPlacement, prefixCls + "-content-" + actualAlignment, prefixCls + "-content-" + (visible ? 'visible' : 'hidden'), prefixCls + "-content-" + (_this.isOuterAlignment ? 'outer' : 'inner'), (_b = {},
                _b[prefixCls + "-content-no-animation"] = !animated,
                _b));
            var containerCls = classNames(prefixCls + "-container", containerClassName, (_c = {},
                _c[prefixCls + "-container-" + (visible ? 'visible' : 'hidden')] = containerFollowVisible,
                _c));
            var shouldDestroyDom = !_this.forceRenderDom && destroyOnClose && !decideKeepDom.call(_this, DROPDOWN_TRANSITION_OUT_DURATION, visible);
            return (h(Trigger, { action: _this.hide, className: cls, style: style, domRef: _this.rootDomRef, testId: testId },
                h("div", { onMouseEnter: trigger === 'hover' ? _this.handleMouseEnter : undefined, onMouseLeave: trigger === 'hover' ? _this.handleMouseLeave : undefined },
                    h("div", { className: containerCls, style: containerStyle, onClick: trigger === 'click' ? _this.toggle : undefined }, children),
                    h(DropdownContext.Provider, { value: getDropdownContext({ visible: visible, zIndex: zIndex + 1 }) },
                        h(DocumentDomContext.Consumer, null, function (doc) { return (shouldDestroyDom ? null : ReactDOM.createPortal(h("div", { className: contentCls, style: __assign(__assign({ zIndex: zIndex }, dropContentMetrics), dropContentStyle), onClick: _this.hide, ref: _this.dropContentRef, onKeyDown: _this.handleKeydown, tabIndex: -1, "data-testid": dropContentTestId }, dropContent), containerDom || (doc === null || doc === void 0 ? void 0 : doc.body) || document.body)); })))));
        };
        return _this;
    }
    Dropdown.getDerivedStateFromProps = function (props, state) {
        if (props.disabled) {
            return { visible: false };
        }
        return null;
    };
    Object.defineProperty(Dropdown.prototype, "isOuterAlignment", {
        get: function () {
            return outerAlignments.includes(this.props.alignment);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Dropdown.prototype, "isControlled", {
        get: function () {
            return typeof this.props.visible === 'boolean';
        },
        enumerable: false,
        configurable: true
    });
    Dropdown.prototype.componentDidMount = function () {
        this.forceUpdate();
        if (typeof ResizeObserver === 'function') {
            this.resizeObserver = new ResizeObserver(this.refreshDropContentMetric);
        }
    };
    Dropdown.prototype.componentDidUpdate = function (prevProps, prevState, snapshot) {
        var _this = this;
        var propVisible = this.props.visible;
        var visible = this.isVisible();
        var prevVisible = this.prevVisible;
        this.prevVisible = visible;
        if (!prevVisible && visible) {
            if (propVisible) {
                triggerMicroTask(function () { return _this.show(true); });
            }
            this.focusSelf();
            this.handleEnteringAnimation();
            this.addListeners();
        }
        if (prevVisible && !visible) {
            this.restorePreviousFocus();
            this.clearListeners();
        }
    };
    Dropdown.prototype.addListeners = function () {
        var _a, _b, _c;
        (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.observe(this.rootDomRef.current);
        (_b = this.resizeObserver) === null || _b === void 0 ? void 0 : _b.observe(this.dropContentRef.current);
        (_c = this.cancelPositionChangeChecker) === null || _c === void 0 ? void 0 : _c.call(this);
        this.cancelPositionChangeChecker = checkPositionChange(this.rootDomRef.current, this.hide);
        this.dropContentObserver.observe(this.dropContentRef.current, { subtree: true, childList: true });
    };
    Dropdown.prototype.clearListeners = function () {
        var _a, _b;
        (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        (_b = this.cancelPositionChangeChecker) === null || _b === void 0 ? void 0 : _b.call(this);
        this.dropContentObserver.disconnect();
    };
    Dropdown.prototype.componentWillUnmount = function () {
        this.clearListeners();
    };
    Dropdown.prototype.getSnapshotBeforeUpdate = function () {
        return this.dropContentRef.current;
    };
    Dropdown.prototype.handleEnteringAnimation = function () {
        this.fixAnimationFlickeringOnIOS();
        this.blockPointerEventsDuringAnimation();
    };
    Dropdown.prototype.blockPointerEventsDuringAnimation = function () {
        var _this = this;
        this.isInAnimation = true;
        var style = this.dropContentRef.current.style;
        var pointerEvents = style.pointerEvents;
        style.pointerEvents = 'none';
        setTimeout(function () {
            style.pointerEvents = pointerEvents;
            _this.isInAnimation = false;
        }, DROPDOWN_TRANSITION_IN_DURATION);
    };
    Dropdown.prototype.fixAnimationFlickeringOnIOS = function () {
        if (!shouldFixAnimationFlickering) {
            return;
        }
        var style = this.dropContentRef.current.style;
        setTimeout(function () {
            var opacity = style.opacity, transform = style.transform, transition = style.transition;
            style.opacity = '0.99';
            style.transform = 'translate(0)';
            style.transition = 'none';
            setTimeout(function () {
                style.opacity = opacity;
                style.transform = transform;
                style.transition = transition;
            }, 0);
        }, DROPDOWN_TRANSITION_IN_DURATION);
    };
    Dropdown.prototype.isVisible = function () {
        var _a = this.props, forceVisible = _a.visible, disabled = _a.disabled;
        if (disabled) {
            return false;
        }
        var stateVisible = this.state.visible;
        return typeof forceVisible === 'boolean' ? forceVisible : stateVisible;
    };
    Dropdown.prototype.traverseFocus = function (e) {
        var container = this.dropContentRef.current;
        if (!container) {
            return;
        }
        traverseFocusIn(container, e);
    };
    Dropdown.prototype.isFocusInside = function () {
        var _a;
        return (_a = this.dropContentRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement);
    };
    Dropdown.prototype.focusSelf = function () {
        var _this = this;
        if (this.props.autoFocus && this.isVisible() && !this.isFocusInside()) {
            saveFocus();
            setTimeout(function () {
                var _a;
                !_this.isFocusInside() && ((_a = _this.dropContentRef.current) === null || _a === void 0 ? void 0 : _a.focus());
            }, DROPDOWN_TRANSITION_IN_DURATION);
        }
    };
    Dropdown.prototype.restorePreviousFocus = function () {
        if (this.props.autoFocus) {
            restoreFocus();
        }
    };
    Dropdown.prototype.removePreviousClampedSize = function (callback) {
        this.setState({
            dropContentMetrics: __assign(__assign({}, this.state.dropContentMetrics), { height: undefined, width: undefined })
        }, callback);
    };
    Dropdown.prototype.calcHorizontalMetric = function (rootDomRect, dropContentDomRect) {
        var _a = this.props, alignment = _a.alignment, matchWidth = _a.matchWidth, viewportMargin = _a.viewportMargin;
        var rootWidth = rootDomRect.width, left = rootDomRect.left, right = rootDomRect.right;
        var selfWidth = dropContentDomRect.width;
        var innerWidth = window.innerWidth;
        var viewportGap = viewportMargin;
        var viewportRight = innerWidth - viewportGap;
        var actualAlignment = alignment;
        if (alignment === 'auto') {
            var rightAvailableSpace = viewportRight - left;
            var leftAvailableSpace = right - viewportGap;
            if (selfWidth > rightAvailableSpace) {
                actualAlignment = rightAvailableSpace >= leftAvailableSpace ? 'left' : 'right';
            }
            else {
                actualAlignment = 'left';
            }
        }
        else if (alignment === 'auto-outer') {
            actualAlignment = left - selfWidth - viewportGap < 0 ? 'right-outer' : 'left-outer';
        }
        var result = {
            actualAlignment: actualAlignment,
            metric: {}
        };
        if (matchWidth && !this.isOuterAlignment) {
            result.metric.left = left;
            result.metric.width = rootWidth;
            return result;
        }
        var viewportWidth = innerWidth - 2 * viewportGap;
        if (actualAlignment === 'left') {
            if (selfWidth > viewportWidth) {
                result.metric.left = viewportGap;
                result.metric.width = viewportWidth;
            }
            else if (left + selfWidth > viewportRight) {
                result.metric.left = viewportRight - selfWidth;
            }
            else {
                result.metric.left = left;
            }
            return result;
        }
        if (actualAlignment === 'right') {
            if (selfWidth > viewportWidth) {
                result.metric.left = viewportGap;
                result.metric.width = viewportWidth;
            }
            else if (right - selfWidth < viewportGap) {
                result.metric.left = viewportGap;
            }
            else {
                result.metric.left = right - selfWidth;
            }
            return result;
        }
        if (actualAlignment === 'center') {
            result.metric.left = left + (rootWidth - selfWidth) / 2;
        }
        else if (actualAlignment === 'left-outer') {
            result.metric.left = left - selfWidth;
        }
        else if (actualAlignment === 'right-outer') {
            result.metric.left = right;
        }
        return result;
    };
    Dropdown.prototype.calcVerticalMetric = function (rootDomRect, dropContentDomRect) {
        var _a = this.props, placement = _a.placement, viewportMargin = _a.viewportMargin;
        var bottom = rootDomRect.bottom, top = rootDomRect.top;
        var height = dropContentDomRect.height;
        var viewportGap = viewportMargin;
        var resultHeight;
        var resultTop = 0;
        var actualPlacement = 'bottom';
        var isOuter = this.isOuterAlignment;
        var tryTop = function () {
            var anchor = isOuter ? bottom : top;
            resultTop = Math.max(viewportGap, anchor - height);
            resultHeight = anchor - resultTop;
            actualPlacement = 'top';
            return resultHeight;
        };
        var tryBottom = function () {
            resultTop = isOuter ? top : bottom;
            resultHeight = Math.min(height, window.innerHeight - viewportGap - resultTop);
            actualPlacement = 'bottom';
            return resultHeight;
        };
        if (placement === 'top') {
            tryTop();
        }
        else if (placement === 'bottom') {
            tryBottom();
        }
        else if (placement === 'auto') {
            if (tryBottom() === height) {
            }
            else if (tryTop() === height) {
            }
            else {
                tryBottom() >= tryTop() ? tryBottom() : tryTop();
            }
        }
        return {
            metric: {
                top: resultTop + "px",
                height: resultHeight && resultHeight + "px"
            },
            actualPlacement: actualPlacement,
        };
    };
    Dropdown.prototype.setStateVisible = function (visible) {
        this.setState({ visible: visible });
        this.props.onVisibleChange(visible);
    };
    Dropdown.prototype.consumeKeep = function (e) {
        var nativeEvent = e.nativeEvent;
        var result = Boolean(nativeEvent._dui_dropdown_keep);
        delete nativeEvent._dui_dropdown_keep;
        return result;
    };
    Dropdown.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    Dropdown.contextType = DropdownContext;
    return Dropdown;
}(React.Component));
export default Dropdown;
Dropdown.defaultProps = {
    prefixCls: 'dui-dropdown',
    alignment: 'auto',
    matchWidth: false,
    disabled: false,
    onVisibleChange: emptyFn,
    placement: 'auto',
    animated: true,
    trigger: 'click',
    destroyOnClose: true,
    autoFocus: true,
    viewportMargin: 2,
};
